import { defineStore, storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';

import { getCustomPropertyValue } from '@rygen/rygen-ui/util';

import { MessageSpecification } from '@/model';

const APP_MESSAGE_HEIGHT = Number.parseFloat(
  getCustomPropertyValue('--app-message-height').replace('px', ''),
);

export interface MessageStore {
  addMessage: (newVal: MessageSpecification) => void;
  clearMessages: () => void;
  messages: Ref<MessageSpecification[]>;
  removeMessage: (messageId: string) => void;
  visibleMessages: Ref<MessageSpecification[]>;
}

export const isMessageVisible = (message: MessageSpecification) => {
  if (typeof message.visible === 'function') {
    return message.visible();
  }

  // undefined or true -> visible
  return message.visible !== false;
};

const _useMessageStore = defineStore('message', (): MessageStore => {
  const _messages: Ref<Map<string, MessageSpecification>> = ref(new Map());

  const messages = computed(() => {
    const values = _messages.value.values();
    return [...values];
  });

  const visibleMessages = computed(() => {
    return messages.value.filter(isMessageVisible);
  });

  const clearMessages = () => {
    _messages.value.clear();
  };

  const addMessage = (newVal: MessageSpecification) => {
    if (!newVal.text && !newVal.textSlot) {
      throw new Error(
        'One of `text` or `textSlot` is required to display a message',
      );
    }

    _messages.value.set(newVal.id, newVal);
  };

  const removeMessage = (messageId: string) => {
    _messages.value.delete(messageId);
  };

  watch(
    visibleMessages,
    (newVal) => {
      const numMessages = newVal.length;
      const cumulativeAppMessageHeight = APP_MESSAGE_HEIGHT * numMessages;
      document.documentElement.style.setProperty(
        '--cumulative-app-messages-height',
        `${cumulativeAppMessageHeight}px`,
      );
    },
    { immediate: true },
  );

  return {
    addMessage,
    clearMessages,
    messages,
    removeMessage,
    visibleMessages,
  };
});

export const useMessageStore = (): MessageStore => {
  const store = _useMessageStore();
  const { messages, visibleMessages } = storeToRefs(store);
  const { addMessage, clearMessages, removeMessage } = store;

  return {
    addMessage,
    clearMessages,
    messages,
    removeMessage,
    visibleMessages,
  };
};
